import { ContractService } from 'src/app/services/contracts/contract.service';
import { DxButtonModule, DxDataGridModule, DxPopupComponent, DxPopupModule } from 'devextreme-angular';
import { ContractSelectorService } from './../../services/contracts/contract-selector.service';
import { Component, EventEmitter, Input, OnInit, Output, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { StatusPillComponent } from "../status-pill/status-pill.component";
import { ContractStatusConfig, CONTRACT_STATUS_IN_PROGRESS_ID } from "src/app/constants/status-pill-configs";
import { GpdPaginationComponent } from '../gpd-pagination/gpd-pagination.component';
import { emit } from 'process';
import { Router } from '@angular/router';
import { GpdButtonComponent } from '../gpd-button/gpd-button.component';
import { CommonModule } from '@angular/common';
import { ContractFormPresetState } from 'src/app/pages/contracts/contract-form/contract-form-preset';


export interface CreateTo  {
    id: number;
    target: string;
}

@Component({
    selector: 'app-contract-selector-popup',
    imports: [
        CommonModule,
        DxPopupModule,
        DxDataGridModule,
        DxButtonModule,
        StatusPillComponent,
        GpdButtonComponent,
        GpdPaginationComponent,
    ],
    templateUrl: './contract-selector-popup.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './contract-selector-popup.component.css'
})
export class ContractSelectorPopupComponent implements OnInit {
  readonly contractStatusConfig = ContractStatusConfig;

  private _visible = false;
  @Input() set visible(value: boolean) {
    this._visible = value;
    if (value) {
      this.contractSelectorService.getContracts({});
    }
  }
  get visible() { return this._visible; }

  @Input() createTo: CreateTo;
  /** Optional: "Add Contract" opens "Vertrag hinzufügen" with this type locked (used by Gesellschaft erstellen). */
  @Input() preset: ContractFormPresetState | null = null;

  @Output() hidden = new EventEmitter<void>();
  @Output() contractSelected = new EventEmitter<any>();
  /** Fires right before leaving for "Vertrag hinzufügen", so the host can keep its unsaved state. */
  @Output() addingContract = new EventEmitter<void>();

  @ViewChild('contractDatagrid') contractDatagrid: any;
  @ViewChild('contractPopup') contractPopup: DxPopupComponent;

  contracts = [];
  contractTypes = [];
  filter: any;
  entriesPerPage = 10;
  currentPage = 1;

  totalEntries: any;

  constructor(private contractSelectorService: ContractSelectorService, private contractService: ContractService, private router: Router) {

    this.contractSelectorService.getContractTypes({ });

    this.contractSelectorService.nonFrameworkContracts$.subscribe((contracts) => {
      // Ticket 4619: contracts still "In Progress" (draft) aren't finished enough to be linked
      // to a task/property owner yet — keep them out of the picker.
      this.contracts = [...contracts]
        .filter((c) => c.statusId !== CONTRACT_STATUS_IN_PROGRESS_ID)
        .sort((a, b) => b.id - a.id);
      this.totalEntries = this.contracts.length;
    });

    this.contractSelectorService.contractTypes$.subscribe((contractTypes) => {
      this.contractTypes = contractTypes;
    });
  }

  ngOnInit() {}

  get totalPages(): number {
    return Math.ceil(this.totalEntries / this.entriesPerPage) || 1;
  }

  get itemsOnCurrentPage(): number {
    const remaining = this.totalEntries - (this.currentPage - 1) * this.entriesPerPage;
    return Math.min(this.entriesPerPage, remaining);
  }

  onGridContentReady() {
    const count = this.contractDatagrid?.instance?.totalCount();
    if (count !== undefined && count !== this.totalEntries) {
      this.totalEntries = count;
      this.currentPage = 1;
    }
    setTimeout(() => {
      this.contractPopup?.instance?.option('height', 'auto');
    });
  }

  onCellPrepared(e: any) {
    if ((e.columnIndex == 5 && e.rowType == "header") || (e.columnIndex == 1 && e.rowType == "header")) {
      // The indicator span is not always at this position - a column without a header
      // filter has only the sort span, so childNodes[1] is undefined and this threw once
      // per header cell. Guarded rather than re-targeted: childNodes[1] resolves to the
      // sort span on some columns and the header filter on others, so the original
      // intent is ambiguous and worth a deliberate decision.
      let target = e.cellElement?.childNodes?.[0]?.childNodes?.[1] as HTMLElement | undefined;
      target?.style?.setProperty("display", "none");
    }
  }

  onHiding() {
    this.visible = false;
    this.hidden.emit();
  }

  onContractSelected(e: any) {
    this.visible = false;
    this.contractSelected.emit(e.data);
  }

  onAddContract() {
    this.contractService.setPreviousUrl(this.router.url);
    if (this.preset) {
      this.addingContract.emit();
      this.contractService.setFormPreset(this.preset);
      this.router.navigate(['/contracts/add']);
      return;
    }
    try {
      if (this.createTo && this.createTo.target == "task") {
        this.router.navigate(['/contracts/addToTask', this.createTo.id]);
      }
      if (this.createTo && this.createTo.target == "property owner") {
        this.router.navigate(['/contracts/addToPropertyOwner', this.createTo.id]);
      }
    }
    catch(e) {
      console.error("Error while adding contract: ", e);
    }
  }
}
